'use client';

import { createContext, useContext, useEffect, useState, useRef } from 'react';
import type { User, Session, AuthError } from '@supabase/supabase-js';
import { createClient } from '../lib/supabase/client';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  forcedLogoutReason: string | null;
  clearForcedLogoutReason: () => void;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signUp: (email: string, password: string, fullName?: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<{ error: AuthError | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Register the current session as the only active session for this user
async function registerActiveSession(supabase: ReturnType<typeof createClient>, userId: string, sessionToken: string) {
  await supabase
    .from('user_active_sessions')
    .upsert(
      { user_id: userId, session_token: sessionToken, updated_at: new Date().toISOString() },
      { onConflict: 'user_id' }
    );
}

// Check if the current session token is still the active one
async function isSessionStillActive(supabase: ReturnType<typeof createClient>, userId: string, sessionToken: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('user_active_sessions')
    .select('session_token')
    .eq('user_id', userId)
    .single();

  if (error?.code === 'PGRST116' || !data) return true;
  if (error) return true;
  return data.session_token === sessionToken;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [forcedLogoutReason, setForcedLogoutReason] = useState<string | null>(null);
  const supabase = createClient();
  const sessionCheckInterval = useRef<NodeJS.Timeout | null>(null);

  const startSessionPolling = (currentSession: Session) => {
    if (sessionCheckInterval.current) {
      clearInterval(sessionCheckInterval.current);
    }
    const startPolling = () => {
      sessionCheckInterval.current = setInterval(async () => {
        if (!currentSession?.user?.id || !currentSession?.access_token) return;
        const stillActive = await isSessionStillActive(supabase, currentSession.user.id, currentSession.access_token);
        if (!stillActive) {
          await supabase.auth.signOut();
          setSession(null);
          setUser(null);
          clearInterval(sessionCheckInterval.current!);
          window.location.href = '/login?reason=multi_device';
        }
      }, 30000);
    };
    setTimeout(startPolling, 15000);
  };

  const stopSessionPolling = () => {
    if (sessionCheckInterval.current) {
      clearInterval(sessionCheckInterval.current);
      sessionCheckInterval.current = null;
    }
  };

  useEffect(() => {
    // Get the initial session immediately
    supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
      setSession(currentSession);
      setUser(currentSession?.user ?? null);
      setLoading(false);
      if (currentSession) {
        startSessionPolling(currentSession);
      }
    }).catch(() => {
      setLoading(false);
    });

    // Listen for subsequent auth changes (sign in, sign out, token refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      setUser(newSession?.user ?? null);
      // Ensure loading is cleared in case getSession hasn't resolved yet
      setLoading(false);

      if (newSession) {
        startSessionPolling(newSession);
      } else {
        stopSessionPolling();
      }
    });

    return () => {
      subscription.unsubscribe();
      stopSessionPolling();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) return { error };
      if (data.session && data.user) {
        await registerActiveSession(supabase, data.user.id, data.session.access_token);
        startSessionPolling(data.session);
      }
      setSession(data.session);
      setUser(data.user);
      return { error: null };
    } catch (error) {
      if (error instanceof Error && error.message.includes('Failed to fetch')) {
        return {
          error: {
            message: 'Cannot connect to authentication service. Your Supabase project may be paused or inactive. Please check your Supabase dashboard and resume your project if needed.',
            name: 'AuthError',
            status: 500
          } as AuthError
        };
      }
      return { error: error as AuthError };
    }
  };

  const signUp = async (email: string, password: string, fullName?: string) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName || email.split('@')[0],
            avatar_url: '',
          },
        },
      });
      if (error) return { error };
      if (data.session && data.user) {
        await registerActiveSession(supabase, data.user.id, data.session.access_token);
        startSessionPolling(data.session);
      }
      setSession(data.session);
      setUser(data.user);
      return { error: null };
    } catch (error) {
      if (error instanceof Error && error.message.includes('Failed to fetch')) {
        return {
          error: {
            message: 'Cannot connect to authentication service. Your Supabase project may be paused or inactive. Please check your Supabase dashboard and resume your project if needed.',
            name: 'AuthError',
            status: 500
          } as AuthError
        };
      }
      return { error: error as AuthError };
    }
  };

  const signOut = async () => {
    try {
      stopSessionPolling();
      const { error } = await supabase.auth.signOut();
      if (!error) {
        setSession(null);
        setUser(null);
      }
      return { error };
    } catch (error) {
      return { error: error as AuthError };
    }
  };

  const clearForcedLogoutReason = () => setForcedLogoutReason(null);

  return (
    <AuthContext.Provider value={{ user, session, loading, forcedLogoutReason, clearForcedLogoutReason, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
